import { AudioHighlightBookmark } from '@modules/feedback/components/AudioType/AudioFeedbackPlayer/AudioHighlightBookmark';
import { AudioSummaryHighlightBookmark } from '@modules/feedback/components/AudioType/AudioFeedbackPlayer/AudioSummaryHighlightBookmark';
import {
  AudioHighlightMetadata,
  AudioHighlightMetadataType,
} from '@modules/feedback/components/AudioType/types';
import {
  formatAudioTimeFromSeconds,
  trackAudioFeedbackEvents,
} from '@modules/feedback/components/AudioType/utils';
import { FeedbackListViewType } from '@modules/feedback/constants';
import { Slider, Stack, Typography } from '@mui/material';
import { SyntheticEvent } from 'react';
import { Feedback, FeedbackLabel } from 'v2/core/entities/feedback';
import { useGetUser } from 'v2/core/services/user';

interface AudioSeekBarProps {
  value: number;
  duration: number;
  onChange: (value: number | number[]) => void;
  onCommit: (value: number | number[]) => void;
  onJumpToBookmark: (time: number, shouldBuffer: boolean) => void;
  highlightMetadata: AudioHighlightMetadata[];
  isSliderDisabled?: boolean;
  isBookmarkDisabled?: boolean;
  feedback: Feedback;
  selectedLabels: FeedbackLabel[];
  isFeedbackCardView?: boolean;
  feedbackListViewType: FeedbackListViewType;
}

const AudioSeekBar = ({
  value,
  duration,
  onChange,
  onCommit,
  onJumpToBookmark,
  highlightMetadata,
  isSliderDisabled,
  isBookmarkDisabled,
  feedback,
  selectedLabels,
  isFeedbackCardView = false,
  feedbackListViewType,
}: AudioSeekBarProps) => {
  const {
    tenant: { id: tenantID, name: tenantName },
  } = useGetUser();

  const handleOnChangeSeek = (_: Event, newValue: number | number[]) => {
    onChange(newValue);
  };

  const handleOnCommitSeek = (
    _: Event | SyntheticEvent<Element, Event>,
    newValue: number | number[],
  ) => {
    onCommit(newValue);
  };

  const handleJumpBookmark = (time: number, shouldBuffer: boolean) => {
    onJumpToBookmark(time, shouldBuffer);
    trackAudioFeedbackEvents({
      eventName: 'Audio Feedback - Player: Seekbar Bookmark Clicked',
      feedbackID: feedback.id,
    });
  };

  return (
    <Stack spacing={1} direction="row" alignItems="center" width="100%">
      <Typography variant="caption" sx={{ width: '65px' }}>
        {formatAudioTimeFromSeconds(isSliderDisabled ? 0 : value)}
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        width="100%"
        sx={{ position: 'relative' }}
      >
        <Slider
          value={isSliderDisabled ? 0 : value}
          onChange={handleOnChangeSeek}
          onChangeCommitted={handleOnCommitSeek}
          step={1}
          min={0}
          max={duration}
          disabled={isSliderDisabled}
          sx={{
            '.MuiSlider-thumb': {
              zIndex: 3,
              width: '15px',
              height: '15px',
              opacity: isSliderDisabled ? 0 : 1,
              '&:after': {
                width: '15px',
                height: '15px',
              },
            },
            '.MuiSlider-rail': {
              background: '#000',
              height: '10px',
            },
            '.MuiSlider-track': {
              height: '10px',
              opacity: isSliderDisabled ? 0 : 1,
            },
          }}
        />
        {highlightMetadata.map((highlight, index) => {
          if (
            highlight.type === AudioHighlightMetadataType.KeywordSummary ||
            highlight.type === AudioHighlightMetadataType.ReasonSummary
          ) {
            return (
              <AudioSummaryHighlightBookmark
                key={`${highlight.labelID}_${index}_${highlight.summaryIndex}`}
                highlight={highlight}
                highlightMetadata={highlightMetadata}
                duration={duration}
                externalSourceLink={feedback.sourceV2?.externalLink}
                sourceName={feedback.sourceV2?.name ?? ''}
                integrationChannelType={
                  feedback.sourceV2?.integrationChannelType
                }
                tenantName={tenantName}
                tenantID={tenantID}
                onClick={(time) => handleJumpBookmark(time, false)}
                isDisabled={isBookmarkDisabled}
                selectedLabels={selectedLabels}
                feedbackID={feedback.id}
                isFeedbackCardView={isFeedbackCardView}
                feedbackListViewType={feedbackListViewType}
              />
            );
          }

          return (
            <AudioHighlightBookmark
              key={`${highlight.labelID}_${index}`}
              highlight={highlight}
              highlightMetadata={highlightMetadata}
              duration={duration}
              onClick={(time) => handleJumpBookmark(time, true)}
              isDisabled={isBookmarkDisabled}
              selectedLabels={selectedLabels}
            />
          );
        })}
      </Stack>
      <Typography variant="caption" sx={{ width: '65px' }}>
        {formatAudioTimeFromSeconds(duration)}
      </Typography>
    </Stack>
  );
};

export default AudioSeekBar;
